import { useEffect, useState } from "react";
import { Box, Container, Paper, Title, Text, Button, Flex, ThemeIcon, Loader } from "@mantine/core";
import { IconCircleCheck, IconAlertCircle } from "@tabler/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import trpc from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util.ts";

/* Types */

interface VerifyEmailResponse {
  success: boolean;
  message: string;
  user: {
    id: string;
    email: string;
    role: string;
  };
  company?: {
    name: string;
    headquartersCity: string;
    headquartersCountry: string;
    email: string;
  };
}

/* Page */

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<VerifyEmailResponse | null>(null);

  /* Verify Email */

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing verification token.");
      setLoading(false);
      return;
    }

    const verify = async () => {
      try {
        const response = await trpc.auth.verifyEmail.mutate({ token });
        setData(response as VerifyEmailResponse);
      } catch (err) {
        handleTrpcError(err);
        setError("Verification failed or link has expired.");
      } finally {
        setLoading(false);
      }
    };

    verify();
  }, [token]);

  /* Layout */

  return (
    <Box mih="100vh" py={40}>
      <Container size="lg">
        {/* Loading */}
        {loading && (
          <Flex justify="center" align="center" mih="50vh">
            <Loader size="lg" />
          </Flex>
        )}

        {/* Error */}
        {!loading && error && (
          <>
            {/* Header */}
            <Flex direction="column" align="center" gap={4} mb="xl">
              <Title order={1} fz={{ base: 24, sm: 28 }} fw={600} c="#2563EB">
                GT Setu
              </Title>
              <Text fz={{ base: "sm", sm: "md" }} c="#2563EB" fw={500}>
                A Global Trade Sphere Initiative
              </Text>
            </Flex>

            <Paper
              shadow="sm"
              p={{ base: "lg", sm: "xl" }}
              radius="lg"
              maw={650}
              mx="auto"
              style={{ border: "1px solid #E5E7EB" }}
            >
              <Flex direction="column" align="center" gap="md">
                <ThemeIcon
                  size={60}
                  radius={60}
                  color="red"
                  variant="light"
                  styles={{
                    root: {
                      backgroundColor: "#FEE2E2",
                    },
                  }}
                >
                  <IconAlertCircle size={36} color="#EF4444" />
                </ThemeIcon>

                <Title order={2} fz={{ base: 18, sm: 20 }} ta="center">
                  Verification Failed
                </Title>

                <Text fz={{ base: "xs", sm: "sm" }} c="slate.6" ta="center">
                  {error}
                </Text>

                <Flex justify="center" mt="md" w="100%">
                  <Button
                    size="md"
                    px="xl"
                    w="100%"
                    maw={200}
                    onClick={() => navigate("/login")}
                    styles={{
                      root: {
                        backgroundColor: "#2563EB",
                        "&:hover": {
                          backgroundColor: "#1D4ED8",
                        },
                      },
                    }}
                  >
                    Go to Login
                  </Button>
                </Flex>
              </Flex>
            </Paper>
          </>
        )}

        {/* Success */}
        {!loading && !error && data && (
          <>
            {/* Header */}
            <Flex direction="column" align="center" gap={4} mb="xl">
              <Title order={1} fz={{ base: 24, sm: 28 }} fw={600} c="#2563EB">
                GT Setu
              </Title>
              <Text fz={{ base: "sm", sm: "md" }} c="#2563EB" fw={500}>
                A Global Trade Sphere Initiative
              </Text>
            </Flex>

            {/* Success Card */}
            <Paper
              shadow="sm"
              p={{ base: "lg", sm: "xl" }}
              radius="lg"
              maw={650}
              mx="auto"
              style={{ border: "1px solid #E5E7EB" }}
            >
              {/* Success Icon */}
              <Flex justify="center" mb="lg">
                <ThemeIcon
                  size={60}
                  radius={60}
                  color="teal"
                  variant="light"
                  styles={{
                    root: {
                      backgroundColor: "#D1FAE5",
                    },
                  }}
                >
                  <IconCircleCheck size={36} color="#10B981" />
                </ThemeIcon>
              </Flex>

              {/* Success Title */}
              <Title order={2} fz={{ base: 18, sm: 20 }} ta="center" mb="sm">
                Email Verified Successfully!
              </Title>

              {/* Success Description */}
              <Text fz={{ base: "xs", sm: "sm" }} c="slate.6" ta="center" mb="xl">
                Your account has been created successfully. You can now access the platform and start building your
                company profile.
              </Text>

              {/* Verification Summary Box */}
              <Box bg="#F9FAFB" p="md" mb="xl" style={{ border: "1px solid #E5E7EB", borderRadius: "8px" }}>
                <Text fz="sm" fw={600} mb="md">
                  Account Details
                </Text>

                <Flex direction="column" gap="xs">
                  {/* Company Details */}
                  {data.company && (
                    <>
                      <Flex justify="space-between" align="center">
                        <Text fz="sm" c="slate.6">
                          Company Name:
                        </Text>
                        <Text fz="sm" fw={500}>
                          {data.company.name}
                        </Text>
                      </Flex>

                      <Flex justify="space-between" align="center">
                        <Text fz="sm" c="slate.6">
                          Location:
                        </Text>
                        <Text fz="sm" fw={500}>
                          {data.company.headquartersCity}, {data.company.headquartersCountry}
                        </Text>
                      </Flex>

                      <Flex justify="space-between" align="center">
                        <Text fz="sm" c="slate.6">
                          Company Email:
                        </Text>
                        <Text fz="sm" fw={500}>
                          {data.company.email}
                        </Text>
                      </Flex>
                    </>
                  )}
                </Flex>
              </Box>

              {/* Proceed Button */}
              <Flex justify="center">
                <Button
                  size="md"
                  px="xl"
                  w="100%"
                  maw={200}
                  onClick={() => navigate("/login")}
                  styles={{
                    root: {
                      backgroundColor: "#2563EB",
                      "&:hover": {
                        backgroundColor: "#1D4ED8",
                      },
                    },
                  }}
                >
                  Go to Login
                </Button>
              </Flex>
            </Paper>
          </>
        )}
      </Container>
    </Box>
  );
}
